import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Plane, Vector2Tuple, Vector3 } from 'three';

import { ThreeEvent } from '@react-three/fiber';
import { Polygon, PolygonProps, useDollhouse } from '@spazious/dollhouse-engine';
import { useEventCallback } from '@spazious/react-hooks';

export interface DrawPolygonEvent {
  originalEvent: ThreeEvent<PointerEvent> | PointerEvent;
  points: Vector2Tuple[];
  end?: boolean;
}

export interface PolygonDrawControlsProps extends Omit<PolygonProps, 'points'> {
  onDraw?: (evt: DrawPolygonEvent) => void;
}

export const PolygonDrawControls: React.FC<PolygonDrawControlsProps> = memo(
  ({ onDraw, ...restProps }) => {
    const camera = useDollhouse(({ camera }) => camera);
    const pointer = useDollhouse(({ pointer }) => pointer);
    const raycaster = useDollhouse(({ raycaster }) => raycaster);
    const events = useDollhouse(({ events }) => events);
    const plane = useMemo(() => new Plane(camera.up, 0), [camera.up]);

    const points = useRef<Vector2Tuple[]>([]);
    const [closedLoop, setClosedLoop] = useState<Vector2Tuple[]>([]);

    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const getPoint = useEventCallback(() => {
      const point = new Vector3();
      raycaster.setFromCamera(pointer, camera);
      raycaster.ray.intersectPlane(plane, point);
      const result = [point.x, point.z] as [number, number];
      return result;
    });

    const elem = events.connected as HTMLElement | undefined;

    const updateClosedLoop = useEventCallback((points: Vector2Tuple[]) => {
      setClosedLoop(points.length ? [...points, points[0]] : []);
    });

    useEffect(() => {
      const handleDoubleClick = (event: PointerEvent) => {
        onDraw?.({
          originalEvent: event,
          points: [...points.current],
          end: true,
        });
      };

      const handleSingleClick = (event: PointerEvent, point: Vector2Tuple) => {
        points.current.push(point);

        onDraw?.({
          originalEvent: event,
          points: [...points.current],
        });

        updateClosedLoop(points.current);
      };

      const handlePointerDown = (event: PointerEvent) => {
        const point = getPoint();

        // double tap detection
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          handleDoubleClick(event);
        } else {
          timerRef.current = setTimeout(() => {
            timerRef.current = null;
            // process single click
            handleSingleClick(event, point);
          }, 500);
        }
      };

      const handlePointerMove = (event: PointerEvent) => {
        if (!points.current.length) {
          return;
        }

        const newPoints = [...points.current, getPoint()];
        onDraw?.({
          originalEvent: event,
          points: newPoints,
        });

        updateClosedLoop(newPoints);
      };

      const handlePointerUp = (event: PointerEvent) => {
        if (!points.current.length) {
          return;
        }

        const newPoints = [...points.current, getPoint()];
        onDraw?.({
          originalEvent: event,
          points: newPoints,
        });

        updateClosedLoop(newPoints);
      };

      elem?.addEventListener('pointermove', handlePointerMove);
      elem?.addEventListener('pointerdown', handlePointerDown);
      elem?.addEventListener('pointerup', handlePointerUp);
      return () => {
        elem?.removeEventListener('pointerdown', handlePointerDown);
        elem?.removeEventListener('pointermove', handlePointerMove);
        elem?.removeEventListener('pointerup', handlePointerUp);
      };
    }, [elem, getPoint, onDraw, updateClosedLoop]);

    return (
      <group>
        {closedLoop.length > 2 && (
          <Polygon color="orange" lineWidth={2} points={closedLoop} {...restProps} />
        )}
      </group>
    );
  }
);
